import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CustomButton } from "src/design";
import { isPreviewableProof } from "src/business/payment/proofFile";
import {
  proofFileName,
  saveBlobAs,
} from "src/business/payment/proofDownload";
import { resolvePaymentErrorKey } from "src/business/payment/paymentErrors";
import { downloadAdminProof } from "src/data/admin/queries/payment";
import { colorTextError, colorTextMuted } from "src/design/tokens";

interface ProofPreviewDialogProps {
  /** The payment whose proof to show; `null` keeps the dialog closed. */
  paymentId: string | null;
  onClose: () => void;
}

/**
 * The proof, fetched as bytes and shown from them.
 *
 * Nothing ever points an `<img src>` at the API: a credential-less request
 * would come back 403, and it would scatter proof URLs through the DOM — the
 * opposite of what the storage layer was built to guarantee. The one object URL
 * that exists lives as long as the dialog and is revoked when it closes.
 *
 * Only JPEG and PNG are rendered. HEIC displays in no browser and a blob-backed
 * PDF in an iframe behaves inconsistently across them, so both are handed to
 * the browser as downloads instead.
 */
export const ProofPreviewDialog = ({
  paymentId,
  onClose,
}: ProofPreviewDialogProps) => {
  const { t } = useTranslation();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [downloadOnly, setDownloadOnly] = useState(false);

  useEffect(() => {
    if (!paymentId) return;

    let revoked = false;
    let createdUrl: string | null = null;

    const load = async () => {
      setIsLoading(true);
      setErrorKey(null);
      setDownloadOnly(false);
      try {
        const { blob, contentType } = await downloadAdminProof(paymentId);

        if (!isPreviewableProof(contentType)) {
          saveBlobAs(blob, proofFileName(paymentId, contentType));
          setDownloadOnly(true);
          return;
        }

        createdUrl = URL.createObjectURL(blob);
        if (revoked) {
          URL.revokeObjectURL(createdUrl);
          return;
        }
        setObjectUrl(createdUrl);
      } catch (error) {
        setErrorKey(resolvePaymentErrorKey(error));
      } finally {
        setIsLoading(false);
      }
    };

    void load();

    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      setObjectUrl(null);
    };
  }, [paymentId]);

  return (
    <Dialog open={!!paymentId} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t("payments.admin.proof.title")}
      </DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && errorKey && (
          <Typography variant="body2" sx={{ color: colorTextError }}>
            {t(errorKey)}
          </Typography>
        )}

        {!isLoading && downloadOnly && (
          <Typography variant="body2" sx={{ color: colorTextMuted }}>
            {t("payments.admin.proof.downloaded")}
          </Typography>
        )}

        {!isLoading && objectUrl && (
          <Box
            component="img"
            src={objectUrl}
            alt={t("payments.admin.proof.title")}
            sx={{ width: "100%", height: "auto", display: "block" }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CustomButton color="inverted" onClick={onClose}>
          {t("payments.admin.proof.close")}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};
